import { Box, Link, Modal, ModalBody, Paragraph, Stack, Text } from '@customerio/pluma-components/react';
import type { PlumaManagedModalProps } from '@customerio/pluma-components/react';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { useMountEffect } from '@/hooks/state/use-mount-effect';
import type { BillingPlan } from '@/services/billing/types';
import { formatNumber } from '@/utils/number';

const PRICING_URL = 'https://customer.io/pricing/';

/** The per-thousand overage price for a plan's rate string (dollars per unit × 1000). */
function perThousand(rate: string): string {
  return formatNumber(Number(rate) * 1000);
}

export type PlanIncludedModalData = {
  plan: BillingPlan;
  /** When on, profiles replace contacts and the separate objects allowance is hidden. */
  newPipelinesObjectsPricing: boolean;
};

/**
 * The "What's included in your plan" managed modal: the plan's profile/contact, object, and message
 * allowances with their per-thousand overage rates. Ports the pinned-plan modal from the Ember signup
 * form / plan-name-header. Informational — the only exit is the modal's dismiss control.
 */
export function PlanIncludedModal({ data, modalState }: PlumaManagedModalProps<PlanIncludedModalData>) {
  const { plan, newPipelinesObjectsPricing } = data;
  const { account, user } = useAuth();
  const { track } = useAnalytics();

  // Fired here rather than from each link that opens the modal, so every entry point is covered and a
  // new caller cannot forget it. More than one link opens this.
  useMountEffect(() => {
    void track('plan_billing', 'includedInPlanViewed', {
      'Account Id': account.id,
      Requester: `${user.first_name} ${user.last_name}`,
      'Requester Email': user.email,
      'Requester User Id': user.id,
    });
  });

  const profileWord = newPipelinesObjectsPricing ? 'Profiles' : 'Contacts';
  const profileWordLower = newPipelinesObjectsPricing ? 'profiles' : 'contacts';
  // `planned_custom_smtp` is a raw wire field on Stripe plans, not on the shared BillingPlan shape.
  const plannedCustomSmtp =
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    'planned_custom_smtp' in plan && (plan as { planned_custom_smtp?: boolean }).planned_custom_smtp === true;

  return (
    <Modal state={modalState} title="What's included in your plan">
      <ModalBody>
        <Stack gap="200">
          <div>
            <Paragraph weight="bold" m="0" mb="050">
              {newPipelinesObjectsPricing ? 'PROFILES' : 'CONTACTS'}
            </Paragraph>
            <Paragraph m="0" data-test-pinned-plan-contacts-allowed>
              {formatNumber(plan.includedProfiles)} {profileWord}
            </Paragraph>
            <Text size="xs" data-test-pinned-plan-contact-overages>
              Additional monthly {profileWordLower} billed at ${perThousand(plan.profileOverageRate)} per one thousand
            </Text>
          </div>

          {newPipelinesObjectsPricing ? null : (
            <div>
              <Paragraph weight="bold" m="0" mb="050">
                OBJECTS
              </Paragraph>
              <Paragraph m="0" data-test-pinned-plan-objects-allowed>
                {formatNumber(plan.includedObjects)} Object profiles
              </Paragraph>
              <Text size="xs" data-test-pinned-plan-object-overages>
                Additional monthly object profiles billed at ${perThousand(plan.objectOverageRate)} per one thousand
              </Text>
            </div>
          )}

          <div>
            <Paragraph weight="bold" m="0" mb="050">
              MESSAGES
            </Paragraph>
            {plannedCustomSmtp ? (
              <Paragraph m="0" data-test-planned-custom-smtp-text>
                You will be using your own custom SMTP service
              </Paragraph>
            ) : (
              <>
                <Paragraph m="0" data-test-pinned-plan-messages-allowed>
                  {formatNumber(plan.includedMessages)} Messages (fair usage policy)
                </Paragraph>
                <Text size="xs" data-test-pinned-plan-messages-overages>
                  Additional monthly messages billed at $0.12 per one thousand
                </Text>
              </>
            )}
          </div>

          <Box pt="125">
            <Link
              href={PRICING_URL}
              isExternal={true}
              icon="exit"
              iconPosition="trailing"
              iconSize="sm"
              isUnderlined={false}
            >
              View all features
            </Link>
          </Box>
        </Stack>
      </ModalBody>
    </Modal>
  );
}
